import {inject, Injectable} from "@angular/core"
import {DataResolverService} from "@app/material-editor/services/data-resolver.service"
import {JobNodes} from "@cm/job-nodes/job-nodes"
import {cmRenderTaskForPassNames, PictureRenderJobOutput, RenderOutputForPassName} from "@cm/job-nodes/rendering"
import {RenderNodes} from "@cm/render-nodes"
import {SceneNodes, buildRenderGraph} from "@cm/template-nodes"
import {DataObjectWithRelated} from "@cm/utils"
import {graphToJson} from "@cm/utils/graph-json"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {ImageDataObjectInfo, ImageDataObjectThumbnailTriggerService} from "@common/services/generate-thumbnail/image-data-object-thumbnail-trigger.service"
import {Settings} from "@common/models/settings/settings"
import {CM_LOGGER_RENDERING} from "@common/services/logging/index"
import {RenderingServiceCreateJobGQL} from "@common/services/rendering/rendering.generated"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {JobType} from "@generated"

@Injectable({
    providedIn: "root",
})
export class RenderingService {
    private readonly createJobGql = inject(RenderingServiceCreateJobGQL)
    private readonly uploadGqlService = inject(UploadGqlService)
    private readonly dataResolver = inject(DataResolverService)
    private readonly imageDataObjectThumbnailTrigger = inject(ImageDataObjectThumbnailTriggerService)
    private readonly logger = inject(CM_LOGGER_RENDERING)

    async submitRenderJob(data: {
        nodes: SceneNodes.SceneNode[]
        fullQuality: boolean
        passes: readonly RenderNodes.PassName[]
        name: string
        organizationLegacyId: number
        queueGroup?: string
    }) {
        const jobName = `RenderJob: ${data.name}`
        const {mainRenderTask, shadowMaskRenderTask, combinedRenderTask} = await this.createRenderTask(data)
        const jobGraph = JobNodes.jobGraph<PictureRenderJobOutput>(combinedRenderTask, {
            platformVersion: Settings.APP_VERSION,
            progress: JobNodes.progressGroupNoWeights(shadowMaskRenderTask ? [mainRenderTask, shadowMaskRenderTask] : [mainRenderTask], {
                output: mainRenderTask,
            }),
        })

        return mutateThrowingErrors(this.createJobGql)({
            input: {
                name: jobName,
                organizationLegacyId: data.organizationLegacyId,
                graph: graphToJson(jobGraph, this.logger),
            },
        }).then(({createJob}) => createJob)
    }

    async createRenderTask(data: {
        nodes: SceneNodes.SceneNode[]
        fullQuality: boolean
        passes: readonly RenderNodes.PassName[]
        name: string
        organizationLegacyId: number
        previewForProgressValue?: number[]
        queueGroup?: string
    }) {
        const imageDataObjectsById = new Map<string, ImageDataObjectInfo>()
        const registerImageDataObject = (dataObjectWithRelated: DataObjectWithRelated, textureSetRevisionId?: string) => {
            const existing = imageDataObjectsById.get(dataObjectWithRelated.id)
            if (existing) {
                if (textureSetRevisionId !== undefined) existing.associatedTextureSetRevisionIds.push(textureSetRevisionId)
            } else {
                imageDataObjectsById.set(dataObjectWithRelated.id, {
                    dataObjectWithRelated,
                    associatedTextureSetRevisionIds: textureSetRevisionId !== undefined ? [textureSetRevisionId] : [],
                })
            }
        }

        const mainRenderGraph = await buildRenderGraph(
            {...data, renderPurpose: "final", verifySchema: true, registerImageDataObject},
            this.dataResolver.resolve,
            this.logger,
        )
        const mainRenderTask = await this._createRenderTask(mainRenderGraph, data)

        let shadowMaskRenderTask: JobNodes.TypedTaskNode<RenderOutputForPassName<"ShadowCatcher">> | null = null
        if (mainRenderGraph.session.options.passes?.includes("ShadowCatcher")) {
            const shadowMaskRenderGraph = await buildRenderGraph(
                {
                    ...data,
                    aoMaskPass: true,
                    renderPurpose: "final",
                    verifySchema: true,
                    registerImageDataObject,
                },
                this.dataResolver.resolve,
                this.logger,
            )
            shadowMaskRenderTask = await this._createRenderTask(shadowMaskRenderGraph, data, ["ShadowCatcher"])
        }

        // Texture tiles is only meaningful for the path-traced renderer, and only tiled textures are streamed to
        // it, so make sure every referenced texture actually has tiles generated.
        if (mainRenderGraph.session.options.texture_streaming) {
            void this.imageDataObjectThumbnailTrigger.triggerUpdateJobs(Array.from(imageDataObjectsById.values()), {
                legacyOnly: true,
                skipFailed: true,
            })
        }

        return {
            mainRenderTask,
            shadowMaskRenderTask,
            combinedRenderTask: JobNodes.struct({
                renderPasses: JobNodes.get(mainRenderTask, "renderPasses"),
                preview: JobNodes.get(mainRenderTask, "preview"),
                metadata: JobNodes.get(mainRenderTask, "metadata"),
                aoShadowMaskPass: shadowMaskRenderTask
                    ? JobNodes.get(JobNodes.get(shadowMaskRenderTask, "renderPasses"), "ShadowCatcher")
                    : JobNodes.value(undefined),
            }),
        }
    }

    private async _createRenderTask<T extends RenderNodes.PassName>(
        graph: RenderNodes.Render,
        data: {
            organizationLegacyId: number
            previewForProgressValue?: number[]
            queueGroup?: string
        },
        passes: T[] = [],
    ) {
        const uploadResult = await this.uploadGqlService.createAndUploadDataObject(
            new File([JSON.stringify(graphToJson(graph, this.logger))], "render.json", {type: "application/json"}),
            {
                organizationLegacyId: data.organizationLegacyId,
            },
            {showUploadToolbar: false, processUpload: true},
        )

        return JobNodes.task(cmRenderTaskForPassNames(...passes), {
            input: JobNodes.value({
                renderGraph: JobNodes.dataObjectReference(uploadResult.legacyId),
                customerId: data.organizationLegacyId,
                previewForProgressValue: data.previewForProgressValue,
            }),
            queueGroup: data.queueGroup,
        })
    }
}
